import { Box, CircularProgress, Stack } from "@mui/material";
import React, { useEffect } from "react";
import { useIntersectionObserverRef } from "rooks";
import { CachePolicies } from "use-http";

import { JANZU_URL } from "config/config";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { ListResponse } from "types/api";
import { DetectorBase } from "types/detectors";

import { DetectorItem } from "./detectorItem/DetectorItem";
import { DetectorsListSkeleton } from "./detectorsListSkeleton/DetectorsListSkeleton";
import { EmptyStateDetectorsList } from "./emptyStateDetectorsList/EmptyStateDetectorsList";
import { NotUsedDetectorsYetMessage } from "./notUsedDetectorsYetMessage/NotUsedDetectorsYetMessage";

type DetectorsListProps = {
  loadingListDetectors: boolean;
  dataList: DetectorBase[];
  showLoadingIndicator: boolean;
  fetchMore: IntersectionObserverCallback
};

export function DetectorsList({
  showLoadingIndicator,
  dataList,
  loadingListDetectors,
  fetchMore
}: DetectorsListProps) {
  const [loadMoreRef] = useIntersectionObserverRef(fetchMore);
  const {
    get: getDetectorsCount,
    data: detectorsCount,
    loading: isLoadingDetectorsCount,
  } = useFetchCustom<ListResponse<DetectorBase>>(`${JANZU_URL}reef-api/detectors?page_size=1`, {
    cachePolicy: CachePolicies.NO_CACHE,
  });
  useEffect(() => {
    getDetectorsCount();
  }, [getDetectorsCount]);
  const count = detectorsCount?.count ?? 0;

  const dataItems =
    dataList.length > 0 ? (
      <Stack data-testid="data-items" direction="column">
        {dataList.map((row, idx) => (
          <Box key={row.id} ref={idx === dataList.length - 1 ? loadMoreRef : null}>
            <DetectorItem detector={row} />
          </Box>
        ))}
      </Stack>
    ) : (
        <Typography variant="main" fontWeight="bold" align="center">
          No detectors
        </Typography>
    );


  const detectorsList = (
    <>
      {loadingListDetectors && dataList?.length === 0 ? <DetectorsListSkeleton /> : dataItems}
      {showLoadingIndicator && (
        <Box className="mt-4 flex justify-center">
          <CircularProgress />
        </Box>
      )}
    </>
  );

  const body =
    count > 0 ? (
      <>
        {detectorsList}
        {count <= 20 && dataList.length > 0 && <NotUsedDetectorsYetMessage />}
      </>
    ) : (
      <EmptyStateDetectorsList />
    );

  const isLoadingDetectors = loadingListDetectors && dataList?.length === 0;
  const isLoading = isLoadingDetectors || isLoadingDetectorsCount;

  return (
    <Stack direction="column" spacing={4}>
      {isLoading ? <DetectorsListSkeleton /> : body}
    </Stack>
  );
}
